import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Building2, DollarSign, ArrowRight, Loader2 } from 'lucide-react';

interface PortfolioSummary {
  totalValue: number;
  companiesCount: number;
  topHolding: {
    company_name: string;
    shares: number;
    implied_value: number;
  } | null;
}

interface PortfolioSummaryWidgetProps {
  summary: PortfolioSummary | null;
  loading: boolean;
}

export function PortfolioSummaryWidget({ summary, loading }: PortfolioSummaryWidgetProps) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <Card className="border-border bg-gradient-to-br from-primary/5 to-primary/10">
        <CardContent className="p-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Show empty state if no summary data after loading completes
  if (!summary || summary.companiesCount === 0) {
    return (
      <Card className="border-border bg-gradient-to-br from-primary/5 to-primary/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Portfolio Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No portfolio data available. Your holdings will appear here once linked by your company
            administrator.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border bg-gradient-to-br from-primary/5 to-primary/10">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          Portfolio Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <DollarSign className="h-4 w-4" />
              Total Value
            </div>
            <p className="text-2xl font-bold">R{summary.totalValue.toLocaleString()}</p>
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Building2 className="h-4 w-4" />
              Companies
            </div>
            <p className="text-2xl font-bold">{summary.companiesCount}</p>
          </div>
        </div>

        {summary.topHolding && (
          <div className="pt-2 border-t">
            <p className="text-sm text-muted-foreground mb-1">Top Holding</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{summary.topHolding.company_name}</p>
                <p className="text-sm text-muted-foreground">
                  {summary.topHolding.shares?.toLocaleString()} shares
                </p>
              </div>
              <p className="font-semibold text-primary">
                R{summary.topHolding.implied_value?.toLocaleString() || '—'}
              </p>
            </div>
          </div>
        )}

        <Button
          className="w-full"
          variant="default"
          onClick={() => navigate('/investor-portal/portfolio')}
        >
          View Full Analytics
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}
